import { useState, useEffect } from "react";

// react-router components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";

// Soft UI Dashboard PRO React example components
import BaseNavbarLink from "../BaseNavbar/BaseNavbarLink";
import BaseNavbarMobile from "../BaseNavbar/BaseNavbarMobile";

// Soft UI Dashboard PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// BaseNavbar dropdown menus
import MenuOptions from "./MenuOptions";

// Nucleus Logo
import NucleusLogo from "assets/images/nucleus_logo.png";
import { useSoftUIController } from "hooks/context";
import { useAuth0 } from "@auth0/auth0-react";
import userAPI from "services/api/user";
import { getUserMenuItems } from "utils/setup";
import LogoutButton from "components/Buttons/LogoutButton";
import { List, ListItem, ListItemButton, ListItemText, Menu, Popover, Switch } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";

function BaseNavbar({ routes, user, transparent = false, light = false, menu = false }) {

  const [controller, dispatch] = useSoftUIController();
  const { deviceLayout, userPermissions = [] } = controller;
  const rawMenuItems = getUserMenuItems(userPermissions);

  function menuFilter(route, menuItem) {
    if (!route.in_menu) return false;
    if (!route.key) return false;

    const standardizedKey = route.key.toLowerCase();
    const standardizedType = menuItem.toLowerCase();

    if (standardizedKey === standardizedType) return true;
    if (standardizedKey.startsWith(standardizedType + "-")) return true;
    if (standardizedKey.startsWith(standardizedType + ":")) return true;

    return false;
  }

  const menuItems = rawMenuItems.filter((menuItem) => { // menuItem is key, need to get name in
    const matchingRoutes = routes.filter(r => menuFilter(r, menuItem));
    if (matchingRoutes.length === 0) return false;

    return matchingRoutes.some((route) => {
      if (userPermissions.includes(route.key)) return true;

      if (route.collapse && route.collapse.length > 0) {
        return route.collapse.some(child => {
          return child.in_menu && userPermissions.includes(child.key);
        });
      }

      return false;
    });
  });

  userAPI.loadToken(useAuth0());

  const [menuAnchor, setMenuAnchor] = useState({
    overview: null,
    devices: null,
    clearways: null,
    crowds: null,
    translations: null,
    media: null,
    operations: null,
    campaign_manager: null,
  });

  const openMenu = (name) => (domNodeOrEvent) => {
    const anchor = domNodeOrEvent?.currentTarget ? domNodeOrEvent.currentTarget : domNodeOrEvent;
    if (!anchor) return;

    setMenuAnchor((prev) => ({ ...prev, [name]: anchor }));
  };

  const closeMenu = (name) => () => {
    setMenuAnchor((prev) => ({ ...prev, [name]: null }));
  };

  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(deviceLayout == 'mobile');

  const openMobileNavbar = ({ currentTarget }) => setMobileNavbar(currentTarget.parentNode);
  const closeMobileNavbar = () => setMobileNavbar(false);

  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const handleOpenProfileMenu = (event) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  return (
    <Container>
      <SoftBox
        py={deviceLayout == 'mobile' ? 0.5 : 1}
        px={deviceLayout == 'mobile' ? 1.5 : 4}
        my={deviceLayout == 'mobile' ? 1 : 2}
        mx={deviceLayout == 'mobile' ? 1 : 2}
        width={`calc(100% - ${deviceLayout == 'mobile' ? '-40px' : '16px'} - (40px * 2))`}
        borderRadius="16px"
        shadow={transparent ? "none" : "md"}
        color={light ? "white" : "dark"}
        display="flex"
        justifyContent={deviceLayout == 'mobile' ? "flex-start" : "space-between"}
        alignItems="center"
        position="fixed"
        left={0}
        zIndex={1000}
        sx={({ palette: { transparent: transparentColor, white }, functions: { rgba } }) => ({
          backgroundColor: transparent ? transparentColor.main : rgba(white.main, 1),
          backdropFilter: transparent ? "none" : `saturate(200%) blur(30px)`,
        })}
      >
        <SoftBox component={Link} to="/" sx={{ maxWidth: deviceLayout == 'mobile' ? "105px" : "120px" }} pt={deviceLayout == 'mobile' ? '1.5px' : '3px'}>
          <SoftBox
            component="img"
            src={NucleusLogo}
            width="100%"
            borderRadius="lg"
          />
        </SoftBox>
        <SoftBox color="inherit" display={{ xxs: "none", xl: "flex" }} m={0} p={0}>
          {menuItems.map((menuItem, itemIndex) => {

            return (<BaseNavbarLink
              key={itemIndex}
              name={menuItem}
              routes={routes.filter(r => menuFilter(r, menuItem))}
              openHandler={openMenu(menuItem)}
              closeHandler={closeMenu(menuItem)}
              light={light}
            />)
          })}
        </SoftBox>
        <SoftBox display={{ xxs: "none", xl: "flex" }} alignItems="center">
          <SoftBox
            ml={2}
            sx={{ cursor: "pointer" }}
            component={Link}
            to={"/settings"}
          >
            <Icon fontSize="default" sx={{ verticalAlign: 'middle' }}>settings</Icon>
          </SoftBox>
        </SoftBox>
        <SoftBox
          display={{ xxs: "flex", xl: "none" }}
          justifyContent="space-between"
          width="100%"
          lineHeight={0}
          py={1}
          color="inherit"
        >
          <SoftBox pl={1.5}>
            <SoftBox sx={{ width: '100%', cursor: "pointer" }} onClick={openMobileNavbar}>
              <Icon fontSize="default">{mobileNavbar ? "close" : "menu"}</Icon>
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" gap={1.5}>
            <SoftBox sx={{ cursor: "pointer" }} component={Link} to={"/settings"}>
              <Icon fontSize="default">settings</Icon>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      {menuItems.map((menuItem, itemIndex) => {

        return (<MenuOptions
          key={itemIndex}
          routes={routes.filter(r => menuFilter(r, menuItem))}
          route_path={menuItem}
          open={menuAnchor[menuItem]}
          close={closeMenu(menuItem)}
          openHandler={openMenu(menuItem)}
          noCollapse={routes.filter(r => r?.key && r?.key.includes(menuItem) && !r.collapse).length > 0}
        />)
      })}
      {mobileView && (
        <BaseNavbarMobile routes={routes} menuItems={menuItems} open={mobileNavbar} close={closeMobileNavbar} />
      )}
    </Container>
  );
}

BaseNavbar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  user: PropTypes.object,
  transparent: PropTypes.bool,
  light: PropTypes.bool,
  menu: PropTypes.bool,
};

export default BaseNavbar;